import React, { useState } from 'react';
import { DescriptionList, DescriptionItem } from '../shared/Description';
import { Button, Flex, Text } from '@opendoor/bricks/core';
import { formatCurrency } from '@opendoor/bricks/helpers/currencyHelpers';
import {
  getDurationInSeconds,
  parseDatetimeFromString,
  getApproxDuration,
} from '@opendoor/cinderblocks/helpers/datetime';
import { Modal } from '@opendoor/bricks/complex/Modal';

import {
  getReadableHoaFeatures,
  buyerAgentCommissionModalHeader,
  buyerAgentCommissionModalBody,
} from '../helpers/property';
import { CONSUMER_URL } from 'components/globals';

interface PropertyFeaturesProps {
  modifiedTimestamp?: string | null;
  propertyType?: string | null;
  city?: string | null;
  homeSize?: number | null;
  yearBuilt?: number | null;
  lotSize?: number | null;
  pricePerSqft?: number | null;
  bed?: number | null;
  totalBathrooms?: number | null;
  monthlyHoaFee?: number | null;
  addressId?: string | null;
  hoaFeatures?: Array<string>;
  offerable?: boolean;
  buyerAgentPDP?: boolean | null;
  buyerAgentCommissionAmount?: string;
}

const PropertyFeatures: React.FC<PropertyFeaturesProps> = ({
  city,
  yearBuilt,
  bed,
  modifiedTimestamp,
  totalBathrooms,
  lotSize,
  monthlyHoaFee,
  pricePerSqft,
  homeSize,
  propertyType,
  addressId,
  hoaFeatures,
  offerable,
  buyerAgentPDP,
  buyerAgentCommissionAmount,
}) => {
  const flex = ['0 0 40%', '0 0 40%', '0 0 30%'];
  const lastModifiedDuration = modifiedTimestamp
    ? getDurationInSeconds(parseDatetimeFromString(modifiedTimestamp) as Date, new Date())
    : undefined;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  return (
    <>
      <Text as="h2" fontSize="s3" fontWeight="bold">
        Features
      </Text>
      <DescriptionList>
        {lastModifiedDuration && (
          <DescriptionItem
            flex={flex}
            mb={[5, 6]}
            label="On Opendoor"
            value={getApproxDuration(lastModifiedDuration)}
          />
        )}

        <DescriptionItem flex={flex} mb={[5, 6]} label="Type" value={propertyType} />
        <DescriptionItem flex={flex} mb={[5, 6]} label="City" value={city} />
        <DescriptionItem
          flex={flex}
          mb={[5, 6]}
          label="Home size"
          value={homeSize && `${homeSize.toLocaleString()} sq. ft`}
        />
        <DescriptionItem flex={flex} mb={[5, 6]} label="Built in" value={yearBuilt} />
        <DescriptionItem
          flex={flex}
          mb={[5, 6]}
          label="Lot size"
          value={lotSize && `${lotSize.toLocaleString()} sq. ft.`}
        />
        <DescriptionItem
          flex={flex}
          mb={[5, 6]}
          label="Price per sqft"
          value={pricePerSqft && `${formatCurrency(pricePerSqft)}`}
        />
        <DescriptionItem flex={flex} mb={[5, 6]} label="Beds" value={bed} />
        <DescriptionItem flex={flex} mb={[5, 6]} label="Baths" value={totalBathrooms} />
        <DescriptionItem
          flex={flex}
          mb={[5, 6]}
          label="HOA dues"
          value={monthlyHoaFee && `$${monthlyHoaFee} a month`}
          tooltipContent={getReadableHoaFeatures(monthlyHoaFee, hoaFeatures)}
        />
        {buyerAgentCommissionAmount && (
          <DescriptionItem
            flex={flex}
            mb={[5, 6]}
            label={`Buyer’s agent commission`}
            value={buyerAgentCommissionAmount}
            linkContent={buyerAgentCommissionModalHeader}
            handleOnClick={() => setIsModalOpen(true)}
          />
        )}
      </DescriptionList>
      {offerable && !buyerAgentPDP && (
        <Flex
          display={['none', 'none', 'flex', 'flex']}
          mt={[1, 2]}
          flexDirection={['column', 'column', 'row', 'row']}
        >
          <Button
            flex={['0 0 100%', '0 0 40%', '0 0 25%']}
            aria-label="Start offer"
            onClick={() => {
              window.location.assign(`${CONSUMER_URL}/dashboard/buy/offer?id=${addressId}`);
            }}
            analyticsName="cosmos-pdp-start-offer"
            variant="secondary"
          >
            Start an offer
          </Button>
        </Flex>
      )}
      <Modal
        analyticsName="cosmos-buyer-agent-commission-modal"
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        desktopAndTabletSize="sm"
        headerProps={{ title: buyerAgentCommissionModalHeader }}
      >
        {buyerAgentCommissionModalBody}
      </Modal>
    </>
  );
};

export default PropertyFeatures;
